import { z } from 'zod';

import { DateLikeSchema } from '@vestigas/utils';

import { zodAddressSchema } from './addressValidations';

// A unit code is free text: documents carry unmapped values such as "Lieferung".
const zodUnitSchema = z.string().nullish();

// Country codes in stored documents are not normalised ("D", "DE DE", "STE").
const zodDocumentAddressSchema = zodAddressSchema.extend({
  country: z.string().nullish(),
});

// Ids are hex uuids of any version; an unset id is the empty string.
const zodGuidOrNotSetSchema = z.union([z.literal(''), z.guid()]).nullish();

const zodValueMeasureSchema = z.object({
  measure: zodUnitSchema,
  value: z.number().optional(),
});

const zodUniversalCommunicationSchema = z.object({
  completeNumber: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  type: z.string().nullish(),
});

const zodTradeContactSchema = z.object({
  departmentName: z.string().nullish(),
  jobTitle: z.string().nullish(),
  personName: z.string().nullish(),
  universalCommunication: zodUniversalCommunicationSchema.nullish(),
});

const zodLegalOrganizationSchema = z.object({
  address: zodDocumentAddressSchema.nullish(),
  companyAccountId: zodGuidOrNotSetSchema,
  id: zodGuidOrNotSetSchema,
  issuerAssignedId: z.string().nullish(),
  name: z.string().nullish(),
  universalCommunication: zodUniversalCommunicationSchema.nullish(),
});

const zodAccountSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  typeCode: z.string().nullish(),
});

const zodPartySchema = z.object({
  id: z.string().nullish(),
  legalOrganization: zodLegalOrganizationSchema.nullish(),
  tradeContact: zodTradeContactSchema.nullish(),
});

// Should be something like
// 'CFR', 'CIF', 'CIP', 'CPT', 'DAP', 'DDP', 'DPU', 'EXW', 'FAS', 'FCA', 'FH', 'FOB',
// but we are more lenient as people enter all kinds of stuff here.
const zodDeliveryTypeSchema = z.string();

const zodDeliveryTermsSchema = z.object({
  code: zodDeliveryTypeSchema.nullish(),
});

const zodAgreementSchema = z.object({
  buyer: zodPartySchema,
  deliveryTerms: zodDeliveryTermsSchema.nullish(),
  seller: zodPartySchema,
  trader: zodPartySchema.nullish(),
});

const zodRegistrationIdSchema = z.object({
  country: z.string().nullish(),
  id: z.string().nullish(),
  issuerAssignedId: z.string().nullish(),
});

const zodConsignmentSchema = z.object({
  carrier: zodPartySchema.nullish(),
  movement: z
    .object({
      driver: zodTradeContactSchema.nullish(),
      freightForwarder: zodPartySchema.nullish(),
      means: z.string().nullish(),
      registrationId: z.array(zodRegistrationIdSchema).nullish(),
    })
    .nullish(),
});

const zodExecutionSchema = z.object({
  arrived: DateLikeSchema.nullish(),
  arrivedPlanned: DateLikeSchema.nullish(),
  beginExecution: DateLikeSchema.nullish(),
  beginExecutionPlanned: DateLikeSchema.nullish(),
  beginLoading: DateLikeSchema.nullish(),
  beginLoadingPlanned: DateLikeSchema.nullish(),
  beginUnloading: DateLikeSchema.nullish(),
  beginUnloadingPlanned: DateLikeSchema.nullish(),
  co2Emission: zodValueMeasureSchema.nullish(),
  co2EmissionPlanned: zodValueMeasureSchema.nullish(),
  departure: DateLikeSchema.nullish(),
  departurePlanned: DateLikeSchema.nullish(),
  distance: zodValueMeasureSchema.nullish(),
  distancePlanned: zodValueMeasureSchema.nullish(),
  endExecution: DateLikeSchema.nullish(),
  endExecutionPlanned: DateLikeSchema.nullish(),
  endLoading: DateLikeSchema.nullish(),
  endLoadingPlanned: DateLikeSchema.nullish(),
  endUnloading: DateLikeSchema.nullish(),
  endUnloadingPlanned: DateLikeSchema.nullish(),
  waitingTime: z.number().nullish(),
});

const zodNoteSchema = z.object({
  note: z
    .object({
      content: z
        .object({
          amount: z.number().nullish(),
          comment: z.string().nullish(),
          measure: z.string().nullish(),
          time: z.number().nullish(),
        })
        .nullish(),
    })
    .nullish(),
  type: z
    .enum([
      'amount_pallet_note',
      'comment_note',
      'regie_time_note',
      'waiting_time_note',
      'waitingTimeNote',
    ])
    .nullish(),
});

const zodTradeAddressSchema = zodDocumentAddressSchema.extend({
  id: z.string().nullish(),
  issuerAssignedId: z.string().nullish(),
  lineOne: z.string().nullish(),
});

const zodShippingPartySchema = zodPartySchema.extend({
  tradeAddress: zodTradeAddressSchema.nullish(),
});

const zodShipFromSchema = zodShippingPartySchema.extend({
  consignment: zodConsignmentSchema.nullish(),
});

const zodShipToSchema = zodShippingPartySchema;

const zodDeliverySchema = z.object({
  execution: zodExecutionSchema.nullish(),
  note: z.array(zodNoteSchema).nullish(),
  shipFrom: zodShipFromSchema.optional(),
  shippingMarks: z.unknown().optional(),
  shipTo: zodShipToSchema.optional(),
});

const zodSettlementSchema = z.object({
  payableAccount: zodAccountSchema.nullish(),
  purchaseAccount: zodAccountSchema.nullish(),
  salesAccount: zodAccountSchema.nullish(),
});

// ============================================================================
// Line Item Schema
// ============================================================================

const zodQuantitySchema = z.object({
  quantity: z.number(),
  quantityType: zodUnitSchema,
});

const zodWeightSchema = z.object({
  measure: zodUnitSchema,
  weight: z.number().nullish(),
});

const zodWeighingWeightSchema = zodWeightSchema.extend({
  date: DateLikeSchema.nullish(),
  id: z.string().nullish(),
});

const zodCertificateAuthoritySchema = z.object({
  authorityId: z.string(),
  legalOrganization: zodLegalOrganizationSchema,
});

const zodCertificateSubjectSchema = z.object({
  legalOrganization: zodLegalOrganizationSchema,
  subjectId: z.string(),
});

const zodCertificateSchema = z.object({
  certificateAuthority: zodCertificateAuthoritySchema.optional(),
  certificateSubject: zodCertificateSubjectSchema.nullish(),
  comment: z.string().nullish(),
  expiresAt: DateLikeSchema.nullish(),
  issuedAt: DateLikeSchema.nullish(),
  norms: z.array(z.string()),
  notBefore: z.string().nullish(),
  performanceDeclaration: z.string().nullish(),
  type: z.string().optional(),
  usage: z.string().nullish(),
});

const zodConcreteComponentSchema = z.object({
  comment: z.string().nullish(),
  description: z.string().nullish(),
  manufacturer: zodPartySchema.nullish(),
  name: z.string(),
  quantity: zodValueMeasureSchema.nullish(),
  requested: zodValueMeasureSchema.nullish(),
  type: z.string(),
});

// The API sends an object (legal organization + contact person), not a plain
// string, for the responsible party on a posterior addition.
const zodConcreteResponsibleSchema = z.object({
  legalOrganization: zodLegalOrganizationSchema.nullish(),
  person: zodTradeContactSchema.nullish(),
});

const zodPosteriorAddedSchema = z.object({
  comment: z.string().nullish(),
  consistencyAfter: z.string().nullish(),
  consistencyBefore: z.string().nullish(),
  datetime: DateLikeSchema.nullish(),
  manufacturer: zodPartySchema.nullish(),
  name: z.string(),
  quantity: zodValueMeasureSchema.nullish(),
  reason: z.string().nullish(),
  requested: zodValueMeasureSchema.nullish(),
  responsible: zodConcreteResponsibleSchema.nullish(),
  result: z.string().nullish(),
  retardationTime: z.string().nullish(),
  type: z.string().nullish(),
});

const zodConcreteProductSchema = z.object({
  chlorideClass: z.string().nullish(),
  chlorideContent: zodValueMeasureSchema.nullish(),
  comment: z.string().nullish(),
  components: z.array(zodConcreteComponentSchema).nullish(),
  consistency: z.string().nullish(),
  exposureClass: z.string().nullish(),
  grossDensityClass: z.string().nullish(),
  largestGrain: z.string().nullish(),
  lastValidation: DateLikeSchema.nullish(),
  moistureClass: z.string().nullish(),
  posteriorAdded: z.array(zodPosteriorAddedSchema).nullish(),
  processabilityTime: z.number().nullish(),
  returnedMaterial: zodValueMeasureSchema.nullish(),
  strengthClass: z.string().nullish(),
  strengthDevelopment: z.string().nullish(),
  type: z.string().nullish(),
  usage: z.string().nullish(),
  waterCementRatio: z.number().nullish(),
});

// A single-weighing note carries only one of the two weights.
const zodNoteContentSchema = z.object({
  grossWeight: zodWeighingWeightSchema.nullish(),
  tareWeight: zodWeighingWeightSchema.nullish(),
  weighingParty: z.record(z.string(), z.unknown()).nullish(),
  weighingPerson: z.record(z.string(), z.unknown()).nullish(),
});

const zodDocumentLineSchema = z.object({
  note: z.object({
    content: zodNoteContentSchema,
  }),
});

const zodProductSchema = z.object({
  batch: z.string().nullish(),
  certificate: zodCertificateSchema.nullish(),
  comment: z.string().nullish(),
  concrete: zodConcreteProductSchema.nullish(),
  constructionComponent: z.string().nullish(),
  constructionPlan: z.string().nullish(),
  description: z.string().nullish(),
  ean: z.string().nullish(),
  manufacturer: zodPartySchema.nullish(),
  name: z.string(),
  productionDate: DateLikeSchema.nullish(),
  sellerAssignedId: z.string().nullish(),
});

const zodLineItemSettlementSchema = z.object({
  payableAccount: zodAccountSchema.nullish(),
  purchaseAccount: zodAccountSchema.nullish(),
  salesAccount: zodAccountSchema.nullish(),
});

const zodAdditionalPartyDataSchema = z.record(z.string(), z.unknown());

const zodLineItemSchema = z.object({
  additionalPartyData: zodAdditionalPartyDataSchema.nullish(),
  agreement: z
    .object({
      buyerOrder: z.string().nullish(),
      sellerOrder: z.string().nullish(),
    })
    .nullish(),
  delivery: z
    .object({
      actualDelivery: zodValueMeasureSchema.nullish(),
      handling: z.record(z.string(), z.unknown()).nullish(),
      netWeight: zodWeightSchema.nullish(),
      productUnit: zodQuantitySchema.nullish(),
      remainingDelivery: zodValueMeasureSchema.nullish(),
      requestedDelivery: zodValueMeasureSchema.nullish(),
      shippingMarks: z.unknown().optional(),
    })
    .optional(),
  documentLine: zodDocumentLineSchema.nullish(),
  id: z.string().optional(),
  product: zodProductSchema.optional(),
  settlement: zodLineItemSettlementSchema.nullish(),
});

// ============================================================================
// Main Delivery Note Schemas
// ============================================================================

const zodLogisticsPackageSchema = z.object({
  id: z.string().optional(),
  lineItem: z.array(zodLineItemSchema),
});

const zodHeaderSchema = z.object({
  additionalPartyData: zodAdditionalPartyDataSchema.nullish(),
  date: DateLikeSchema,
  id: z.string(),
  issuer: zodPartySchema,
  technicalIssuer: zodPartySchema.nullish(),
});

const zodTransactionSchema = z.object({
  agreement: zodAgreementSchema,
  delivery: zodDeliverySchema,
  logisticsPackage: z.array(zodLogisticsPackageSchema),
  settlement: zodSettlementSchema.nullish(),
});

const zodDeliveryNoteBodySchema = z.object({
  context: z.string(),
  header: zodHeaderSchema,
  transaction: zodTransactionSchema,
});

/**
 * Makes every key nullish and every array element nullable or `{}`, at any depth.
 * A chain element's body is a diff: it carries only the keys that changed, null
 * for a key that was emptied or removed, and in a list null for an unchanged
 * element and `{}` for a removed one.
 */
function deepPartial(schema: z.ZodType): z.ZodType {
  if (schema instanceof z.ZodObject) {
    const shape = Object.fromEntries(
      Object.entries(schema.shape).map(([key, value]) => [
        key,
        deepPartial(value as z.ZodType).nullish(),
      ]),
    );

    return z.object(shape);
  }

  if (schema instanceof z.ZodArray) {
    return z.array(
      z
        .union([deepPartial(schema.element as z.ZodType), z.strictObject({})])
        .nullable(),
    );
  }

  if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
    return deepPartial(schema.unwrap() as z.ZodType);
  }

  return schema;
}

const zodDeliveryNoteChainBodySchema = z.object({
  transaction: deepPartial(zodTransactionSchema).optional(),
});

// ============================================================================
// DeliveryNoteAdmin Response Schema (API response type)
// ============================================================================

const zodAcceptRoleCoreSchema = z.enum([
  'buyer',
  'carrier',
  'recipient',
  'supplier',
]);

const zodBillingStateSchema = z.enum([
  'fully_billed',
  'partially_billed',
  'unbilled',
]);

const zodAcceptStateSchema = z.enum(['accepted', 'open', 'rejected']);

const zodProcessStateSchema = z.enum([
  'arrived',
  'cancelled',
  'delivered',
  'imported',
  'inTransport',
  'readyForOutput',
  'settled',
]);

const zodAcceptItemSchema = z.object({
  accept: z.literal('valid').nullish(),
  attachments: z
    .array(
      z.object({
        blob: z.object({
          '@type': z.string(),
          contentType: z.string(),
          digest: z.string(),
          length: z.number(),
        }),
        type: z.string(),
      }),
    )
    .optional(),
  dateTime: z.iso.datetime().optional(),
  onBehalf: z.boolean().optional(),
  partialAccept: z
    .object({
      declined: z.number().optional(),
      valid: z.number().optional(),
      wrong: z.number().optional(),
    })
    .optional(),
  path: z.array(z.union([z.string(), z.number()])),
});

const zodPartialAcceptItemSchema = z.object({
  path: z.array(z.union([z.string(), z.number()])),
  value: z.number(),
});

const zodSGWTypeSchema = z.enum([
  'c', // ...
  'cc', // cost center (accounting reference)
  'cr', // ...
  'pr', // permitted site
  'p', // ...
]);

const zodSGWSchema = z.object({
  companyId: z.uuid(),
  type: zodSGWTypeSchema,
  typeId: z.uuid().optional(),
});

const zodSGWCSchema = z.object().loose();

const zodBilledItemSchema = z.object({
  billingDetails: z
    .array(
      z.object({
        billedAmount: z.number().optional(),
        billedDatetime: z.iso.datetime().optional(),
        billedUnit: z.string().optional(),
        invoiceUuid: z.uuid().optional(),
      }),
    )
    .optional(),
  lineItemIndex: z.number(),
  name: z.string().optional(),
  sellerAssignedId: z.string().nullish(),
  totalBilledQuot: z.coerce.number(),
});

const zodAcceptEventSchema = z.object({
  acceptedItems: z.array(zodAcceptItemSchema),
  coords: z.record(z.string(), z.unknown()).optional(),
  dateTime: z.iso.datetime(),
  id: z.uuid(),
});

/**
 * Complete zod schema for a delivery note - the full API response type.
 * This validates the entire structure returned by the delivery note API.
 */
export const zodDeliveryNoteResponseSchema = z.object({
  acceptStates: z.object({
    acceptItems: z.record(
      zodAcceptRoleCoreSchema,
      z.array(zodAcceptItemSchema),
    ),
    acceptStatus: z.record(zodAcceptRoleCoreSchema, z.boolean()),
    partialValueItems: z.record(
      zodAcceptRoleCoreSchema,
      z.array(zodPartialAcceptItemSchema),
    ),
  }),
  archived: z.boolean(),
  archivedOn: z.union([z.iso.datetime(), z.literal('')]),
  assetState: z.object({
    body: zodDeliveryNoteBodySchema,
  }),
  billedItems: z.record(z.string(), z.array(zodBilledItemSchema)).optional(),
  billedState: zodBillingStateSchema,
  companies: z.unknown(),
  createdBy: z.string(),
  createdOn: z.iso.datetime(),
  dlnAcceptState: zodAcceptStateSchema,
  documentType: z.literal('asset_main'),
  id: z.uuid(),
  involvedUsers: z.array(z.string()),
  isMapped: z.boolean(),
  missingSignatures: z.array(z.string()),
  modifiedBy: z.string(),
  modifiedOn: z.iso.datetime(),
  processState: zodProcessStateSchema,
  sgw: z.array(zodSGWSchema),
  sgwc: zodSGWCSchema,
  sipo: z.unknown(),
  sOus: z
    .array(
      z.object({
        id: z.uuid(),
        name: z.string(),
      }),
    )
    .optional(),
  st: z.string(),
  v: z.literal('1.0'),
});

/**
 * Complete zod schema for a delivery note chain - the full API response type.
 * This validates the entire structure returned by the delivery note chains API.
 */
export const zodDeliveryNoteChainResponseSchema = z.object({
  assetId: z.uuid(),
  assetType: z.literal('delivery_note'),
  attachments: z.object({}).optional(), // TODO flesh out
  body: zodDeliveryNoteChainBodySchema,
  createdBy: z.string(),
  createdOn: z.iso.datetime(),
  documentType: z.literal('asset_chain'),
  id: z.uuid(),
  issuerSignature: z.string(),
  meta: z
    .object({
      acceptEvent: zodAcceptEventSchema.optional(),
      action: z
        .object({
          accepted: z.boolean().optional(),
          siteAccRefUpdate: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
  modifiedBy: z.string().optional(),
  modifiedOn: z.iso.datetime().optional(),
  sgw: z.array(zodSGWSchema),
  sgwc: zodSGWCSchema,
  signatureRole: z.string(), // TODO: use party enum here
  subjectKeyIdentifier: z.string(),
});
